import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import Loader from './Loader';

const CLIENT_ID = 'cstack';

interface Section {
  title?: string;
  content: string;
}

interface LegalNoticesProps {
  entryId: string;
  locale?: string;
  sidebar?: boolean;
}

export default function LegalNotices({
  entryId,
  locale = 'en-us',
  sidebar = true,
}: LegalNoticesProps) {
  const intl = useIntl();
  const [contentArray, setContentArray] = useState<Section[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    async function fetchNotice() {
      setLoading(true);
      try {
        const url = new URL(
          `https://d29n4d462tcp7i.cloudfront.net/services/content/v1/policies_notices/entries/${entryId}`,
        );
        url.searchParams.append('locale', locale.toLowerCase());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {'X-Client-Id': CLIENT_ID},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }

        const data = await response.json();
        const arr: Section[] = Array.isArray(data.data.content)
          ? data.data.content
          : [data.data.content];
        setContentArray(arr);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchNotice();
  }, [entryId, locale]);

  if (loading) {
    return <Loader loading={loading} />;
  }

  if (error) {
    return (
      <div className='LegalNoticeError' role='alert'>
        Error loading notice: {error}
      </div>
    );
  }

  if (!entryId) {
    return (
      <div className='LegalNoticeError' role='alert'>
        Missing required entryId
      </div>
    );
  }

  // RTL support for Arabic
  const isRTL = locale === 'ar';

  return (
    <div
      className='LegalNoticeWrapper'
      style={isRTL ? {direction: 'rtl', textAlign: 'right'} : {}}>
      <div className='LegalNoticeLayout'>
        {sidebar && contentArray.length > 1 && (
          <nav aria-label={intl.formatMessage({id: 'Aria.TableOfContents'})}>
            <ul className='LegalNoticeSidebar'>
              {contentArray.map((section, idx) => {
                if (!section.title || !section.title.trim()) return null;
                const slug = section.title
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
                  .trim()
                  .replace(/[\s]+/g, '-')
                  .replace(/^-+|-+$/g, '');
                return (
                  <li key={slug || idx}>
                    <a href={`#${slug}`}>{section.title}</a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        <div className='LegalNoticeBody'>
          {contentArray.map((section, idx) => {
            const slug = section.title
              ? section.title
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
                  .toLowerCase()
                  .trim()
                  .replace(/[\s]+/g, '-')
                  .replace(/^-+|-+$/g, '')
              : `section-${idx}`;

            return (
              <div
                key={slug}
                style={{fontSize: 'initial', fontWeight: 'initial'}}>
                {section.title && (
                  <h2
                    id={slug}
                    style={{
                      fontSize: 'initial',
                      fontWeight: 'bold',
                      margin: 0,
                    }}>
                    {section.title}
                  </h2>
                )}
                <div
                  dangerouslySetInnerHTML={{__html: section.content}}
                  style={{fontSize: 'initial', fontWeight: 'initial'}}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
